import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbAutoComplete } from "abreact/input/AbAutoComplete";
import { AbItem } from "abreact/misc/AbItem";
import { AbDialog } from "abreact/popup/AbDialog";
import { AbIconPicker } from "abreact/input/icon/AbIconPicker";
import { fetchIcons, matchIconToSearchTerm } from "abreact/input/icon/IconUtils";
import { AbWebServices } from "abweb/service/AbWebServices";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";

/**
 * The AbIconInput is an intellisense input control for selecting icons from our image library.
 */

export const AbIconInput = ({
  value,
  onChange,
  filterFunction,
  disabled,
  customStylings,
  pickerDialogProps
}) => {
  const [showPickerDialog, setShowPickerDialog] = useState(false);
  const allImages = useMemo(() => fetchIcons(filterFunction), [filterFunction]);
  const selectedImage = useMemo(() => {
    return value ? AbWebServices.getImageIdentifierManager().getImageIdentifierService().findIdentifierInfo(value) : undefined;
  }, [value]);
  const getImageSuggestions = useCallback(query => {
    if (!query || query.trim().length === 0) {
      return Promise.resolve([]);
    }
    const maxSuggestions = 20;
    return Promise.resolve(allImages.filter(image => matchIconToSearchTerm(query, image)).slice(0, maxSuggestions));
  }, [allImages]);
  const pickImage = async currentValue => {
    setShowPickerDialog(true);
    return currentValue;
  };
  const handleImageSelected = useCallback(image => {
    onChange(image);
  }, [onChange]);
  const renderItem = item => {
    return /*#__PURE__*/React.createElement(AbItem, {
      icon: item.fullId,
      label: item.extra?.semanticName ?? item.fullId
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbAutoComplete, {
    className: "ab-icon-input",
    dataTestId: "ab-icon-input",
    disabled: disabled,
    value: selectedImage ?? undefined,
    onChange: item => {
      onChange(item?.fullId);
    },
    itemRenderer: renderItem,
    valueRenderer: renderItem,
    suggestionsFunction: getImageSuggestions,
    valuePickerFunction: pickImage
  }), showPickerDialog && /*#__PURE__*/React.createElement(AbIconPickerDialog, {
    onChange: image => handleImageSelected(image),
    filterFunction: filterFunction,
    customStylings: customStylings,
    onClose: () => setShowPickerDialog(false),
    dialogProps: pickerDialogProps
  }));
};

/**
 * The AbIconPicker uses the ImageFinder component to select
 * from a collection of images available in the image library.
 */

const AbIconPickerDialog = ({
  onChange,
  filterFunction,
  customStylings,
  onClose,
  dialogProps
}) => {
  const [image, setImage] = useState(undefined);
  const handleImageSelected = useCallback(selectedImage => {
    setImage(selectedImage);
  }, []);
  const handleOkClick = async () => {
    onChange(image);
    setImage(undefined);
    return true;
  };
  const dialogCommonProps = dialogProps ? ReactUtils.extractCommonProps(dialogProps) : undefined;
  const commonProps = ReactUtils.componentProps("ab-icon-picker-dialog", dialogCommonProps);
  return /*#__PURE__*/React.createElement(AbDialog, _extends({
    cancelButton: true,
    okButtonProps: {
      disabled: image === undefined // Disable OK button if no image is selected
    },
    onOk: handleOkClick,
    onClose: onClose,
    title: AbReactResources.iconPicker_selectIcon()
  }, commonProps), /*#__PURE__*/React.createElement(AbIconPicker, {
    filterFunction: filterFunction,
    onSelectImage: selectedImage => handleImageSelected(selectedImage.fullId),
    customStylings: customStylings
  }));
};
//# sourceMappingURL=AbIconInput.js.map
